export default function (Alpine) {

    const _openDropdowns = new Set();

    document.addEventListener('pointerdown', (e) => {
        _openDropdowns.forEach((instance) => {
            if (instance.$root.contains(e.target) || instance._targetEl?.contains(e.target)) {

                instance._clickedInside = true;
            } else {

                if (instance._clickedInside) instance.close();
            }
        });
    });

    Alpine.data('dropdown', () => ({

        open:           false,
        _targetEl:      null,
        _mode:          'click',
        _placement:     'auto',
        _offset:        6,
        _openDelay:     0,
        _closeDelay:    100,
        _closeOnSelect: true,
        _cleanup:       [],
        _closeTimer:    null,
        _openTimer:     null,
        _reposition:    null,
        _hoverTracker:  null,
        _panels:        [],
        _stack:         [],
        _animating:     false,
        _clickedInside: false,

        init() {
            const root = this.$root;
            const self = this;

            self._mode          = root.getAttribute('trigger')         ?? 'click';
            self._placement     = root.getAttribute('placement')       ?? 'auto';
            self._offset        = parseInt(root.getAttribute('offset')        ?? '6',   10);
            self._openDelay     = parseInt(root.getAttribute('open-delay')    ?? '0',   10);
            self._closeDelay    = parseInt(root.getAttribute('close-delay')   ?? '100', 10);
            self._closeOnSelect = root.getAttribute('close-on-select') !== 'false';

            const defaultOpen = root.hasAttribute('open');

            const sel = root.getAttribute('target');
            if (!sel) { console.warn('[dropdown] target eksik'); return; }
            self._targetEl = document.querySelector(sel);
            if (!self._targetEl) { console.warn(`[dropdown] target bulunamadı: ${sel}`); return; }

            document.body.appendChild(root);
            Object.assign(root.style, { position: 'fixed', zIndex: '9999' });

            const menuId = root.id || `dd-${Math.random().toString(36).slice(2,7)}`;
            root.id = menuId;
            root.setAttribute('role', 'menu');
            self._targetEl.setAttribute('aria-haspopup', 'true');
            self._targetEl.setAttribute('aria-controls', menuId);
            self._targetEl.setAttribute('aria-expanded', 'false');

            self._initPanels();
            self._bindTarget();
            self._watch();

            if (defaultOpen) {
                self.$nextTick(() => self._open());
            }
        },

        destroy() {
            this._cleanup.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
            this._cleanup = [];
            this._stopHoverTracker();
            if (this._reposition) {
                window.removeEventListener('resize', this._reposition);
                window.removeEventListener('scroll', this._reposition, true);
            }
            _openDropdowns.delete(this);
        },

        _initPanels() {
            const self = this;
            const root = self.$root;

            self._panels = [];
            self._stack  = ['root'];

            root.querySelectorAll('.dropdown-panel').forEach(panelEl => {
                const id    = panelEl.getAttribute('data-panel') ?? 'root';
                const title = panelEl.getAttribute('data-title') ?? '';

                self._panels.push({ id, el: panelEl, title });

                panelEl.querySelectorAll('[data-panel-back]').forEach(backEl => {
                    backEl.addEventListener('click', (e) => {
                        e.stopPropagation();
                        self.panelBack();
                    });
                });

                panelEl.querySelectorAll('[data-panel-link]').forEach(item => {
                    item.setAttribute('role', 'menuitem');
                    item.setAttribute('tabindex', '-1');
                    item.addEventListener('click', (e) => {
                        e.stopPropagation();
                        if (!item.hasAttribute('disabled')) {
                            self.panelGo(item.getAttribute('data-panel-link'));
                        }
                    });
                });

                panelEl.querySelectorAll('.option:not([data-panel-link])').forEach(item => {
                    item.setAttribute('role', 'menuitem');
                    item.setAttribute('tabindex', '-1');
                    item.addEventListener('click', (e) => {
                        e.stopPropagation();
                        if (item.hasAttribute('disabled') || item.classList.contains('disabled')) return;
                        const value = item.getAttribute('data-value') ?? item.textContent.trim();
                        root.dispatchEvent(new CustomEvent('select', {
                            detail: { value, item, panel: id },
                            bubbles: true,
                        }));
                        if (self._closeOnSelect) self.close();
                    });
                });
            });

            self._syncPanels();
        },

        panelGo(id) {
            const self   = this;
            if (self._animating) return;
            if (!self._panels.find(p => p.id === id)) {
                console.warn(`[dropdown] panel bulunamadı: "${id}"`); return;
            }
            const fromId = self._currentPanelId();
            self._stack.push(id);
            self._animating = true;
            self._transition('forward', fromId, id);
        },

        panelBack() {
            const self = this;
            if (self._animating || self._stack.length <= 1) return;
            const fromId = self._currentPanelId();
            self._stack.pop();
            const toId = self._currentPanelId();
            self._animating = true;
            self._transition('back', fromId, toId);
        },

        panelReset() {
            this._stack = ['root'];
            this._syncPanels();
        },

        _currentPanelId() {
            return this._stack[this._stack.length - 1];
        },

        _transition(dir, fromId, toId) {
            const self = this;
            const dur  = 220;

            const fromEl = self._panels.find(p => p.id === fromId)?.el;
            const toEl   = self._panels.find(p => p.id === toId)?.el;

            if (!toEl) return;

            const wrapper = self.$root.querySelector('.dropdown-panels');
            if (wrapper) wrapper.style.position = 'relative';

            if (fromEl) {
                fromEl.setAttribute('data-active', '');
                fromEl.style.position = 'absolute';
                fromEl.style.inset = '0';
            }

            toEl.setAttribute('data-entering', '');
            toEl.setAttribute('data-active', '');

            requestAnimationFrame(() => {
                toEl.removeAttribute('data-entering');
                if (fromEl) {
                    fromEl.removeAttribute('data-active');
                    fromEl.setAttribute('data-exiting', dir === 'forward' ? 'left' : 'right');
                }

                setTimeout(() => {
                    if (fromEl) {
                        fromEl.style.position = '';
                        fromEl.style.inset = '';
                    }
                    self._animating = false;
                    self._syncPanels();
                    self._position();
                }, dur);
            });

            self._position();
        },

        _syncPanels() {
            const currentId = this._currentPanelId();
            this._panels.forEach(p => {
                const active = p.id === currentId;
                p.el.toggleAttribute('data-active', active);
                p.el.removeAttribute('data-exiting');
                p.el.removeAttribute('data-exit');
                p.el.removeAttribute('data-enter');
            });
        },

        _bindTarget() {
            const self = this;
            const el   = self._targetEl;

            if (self._mode === 'hover') {
                self._hoverTracker = (e) => {
                    if (!self.open) return;
                    const hit  = document.elementFromPoint(e.clientX, e.clientY);
                    const over = el.contains(hit) || self.$root.contains(hit);
                    if (over) self._cancelClose(); else self._scheduleClose();
                };
                self._on(el, 'mouseenter', () => {
                    self._cancelClose();
                    self._scheduleOpen();
                    self._startHoverTracker();
                });
                self._on(el, 'mouseleave', () => { if (!self.open) self._cancelOpen(); });
            } else {
                self._on(el, 'click', (e) => { e.stopPropagation(); self.toggle(); });
            }

            self._on(el, 'keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.toggle(); }
                if (e.key === 'Escape')    self.close();
                if (e.key === 'ArrowDown') { e.preventDefault(); self._open(); self._focusItem(0); }
                if (e.key === 'ArrowUp')   { e.preventDefault(); self._open(); self._focusItem(-1); }
            });

            self._on(self.$root, 'keydown', (e) => {
                if (e.key === 'Escape') {
                    e.stopPropagation();
                    if (self._stack.length > 1) self.panelBack();
                    else { self.close(); el.focus(); }
                    return;
                }
                if (e.key === 'ArrowLeft' && self._stack.length > 1) { e.preventDefault(); self.panelBack(); return; }
                const items = self._activeItems();
                const idx   = items.indexOf(document.activeElement);
                if (e.key === 'ArrowDown') { e.preventDefault(); items[(idx + 1) % items.length]?.focus(); }
                if (e.key === 'ArrowUp')   { e.preventDefault(); items[(idx - 1 + items.length) % items.length]?.focus(); }
                if (e.key === 'Tab')       self.close();
            });
        },

        toggle() { this.open ? this.close() : this._open(); },

        _open() {
            const self = this;
            self._cancelClose();
            self._cancelOpen();
            _openDropdowns.forEach(d => { if (d !== self) d.close(); });
            self.open = true;
            self._clickedInside = false;
            _openDropdowns.add(self);
            self.$nextTick(() => self._position());
        },

        close() {
            if (!this.open) return;
            this._cancelClose();
            this._cancelOpen();
            this.open = false;
            _openDropdowns.delete(this);
            this.$root.removeAttribute('data-placement');
            this._stopHoverTracker();
            setTimeout(() => this.panelReset(), 250);
        },

        _scheduleOpen() {
            const self = this;
            if (self._openDelay <= 0) { self._open(); return; }
            self._openTimer = setTimeout(() => { self._openTimer = null; self._open(); }, self._openDelay);
        },
        _cancelOpen() { if (this._openTimer) { clearTimeout(this._openTimer); this._openTimer = null; } },

        _scheduleClose() {
            if (this._closeTimer) return;
            this._closeTimer = setTimeout(() => { this._closeTimer = null; this.close(); }, this._closeDelay);
        },
        _cancelClose() { if (this._closeTimer) { clearTimeout(this._closeTimer); this._closeTimer = null; } },

        _startHoverTracker() {
            if (this._hoverTracker) document.addEventListener('mousemove', this._hoverTracker, { passive: true });
        },
        _stopHoverTracker() {
            if (this._hoverTracker) document.removeEventListener('mousemove', this._hoverTracker);
        },

        _position() {
            const self    = this;
            const menu    = self.$root;
            const trigger = self._targetEl;
            const gap     = self._offset;

            const tr = trigger.getBoundingClientRect();
            const mW = menu.offsetWidth  || 200;
            const mH = menu.offsetHeight || 100;
            const vW = window.innerWidth;
            const vH = window.innerHeight;

            let top, left, resolved;

            const [primaryPlacement, align] = self._placement.split('-');

            switch (primaryPlacement) {
                case 'top':    resolved='top';    top=tr.top-mH-gap;    left=tr.left; break;
                case 'bottom': resolved='bottom'; top=tr.bottom+gap;    left=tr.left; break;
                case 'left':   resolved='left';   top=tr.top;           left=tr.left-mW-gap; break;
                case 'right':  resolved='right';  top=tr.top;           left=tr.right+gap; break;
                default:
                    resolved = (vH-tr.bottom-gap >= mH || vH-tr.bottom >= tr.top) ? 'bottom' : 'top';
                    top  = resolved === 'bottom' ? tr.bottom+gap : tr.top-mH-gap;
                    left = tr.left;
            }

            if (resolved === 'top' || resolved === 'bottom') {
                if (align === 'center') {
                    left = tr.left + (tr.width / 2) - (mW / 2);
                } else if (align === 'end') {
                    left = tr.right - mW;
                }

                if (left + mW > vW - gap) left = vW - mW - gap;
                if (left < gap)           left = gap;
            } else {
                if (top + mH > vH - gap) top = vH - mH - gap;
                if (top < gap)           top = gap;
            }

            menu.style.top  = `${top}px`;
            menu.style.left = `${left}px`;
            menu.setAttribute('data-placement', resolved);
        },

        _activeItems() {
            const panel = this._panels.find(p => p.id === this._currentPanelId())?.el;
            if (!panel) return [];
            return [...panel.querySelectorAll('[role="menuitem"]:not([disabled]):not(.disabled)')];
        },

        _focusItem(idx) {
            const items = this._activeItems();
            if (!items.length) return;
            this.$nextTick(() => (idx < 0 ? items[items.length-1] : items[idx])?.focus());
        },

        _on(el, type, fn) {
            el.addEventListener(type, fn);
            this._cleanup.push({ el, type, fn });
        },

        _watch() {
            const self = this;
            self.$watch('open', (val) => {
                self._targetEl?.setAttribute('aria-expanded', val ? 'true' : 'false');
                self.$root.toggleAttribute('open', val);
                self.$root.dispatchEvent(new CustomEvent(val ? 'open' : 'close', { bubbles: true }));

                if (val) {
                    if (!self._reposition) {
                        self._reposition = () => { if (self.open) self._position(); };
                        window.addEventListener('resize', self._reposition, { passive: true });
                        window.addEventListener('scroll', self._reposition, { passive: true, capture: true });
                    }
                } else {
                    if (self._reposition) {
                        window.removeEventListener('resize', self._reposition);
                        window.removeEventListener('scroll', self._reposition, true);
                        self._reposition = null;
                    }
                }
            });
        },

    }));
}

/*
KULLANIM:

<button id="btn">Menü ▾</button>

<div x-data="dropdown" target="#btn" trigger="click">
  <div class="dropdown-panels">

    <div class="dropdown-panel" data-panel="root">
      <div class="option" data-value="profile">Profil</div>
      <div class="option" data-panel-link="settings">Ayarlar</div>
      <div class="option" disabled>Devre Dışı</div>
    </div>

    <div class="dropdown-panel" data-panel="settings" data-title="Ayarlar">
      <div class="option" data-value="theme">Tema</div>
      <div class="option" data-panel-link="notif">Bildirimler</div>
    </div>

    <div class="dropdown-panel" data-panel="notif" data-title="Bildirimler">
      <div class="option" data-value="email">E-posta</div>
    </div>

  </div>
</div>

ATTRİBUTELER:
  target            CSS selector        (zorunlu)
  trigger           click | hover       (default: click)
  placement         auto|top|bottom|left|right  (default: auto)
  offset            px                  (default: 6)
  open-delay        ms                  (default: 0)
  close-delay       ms                  (default: 150)
  close-on-select   true | false        (default: true)

  data-panel        panel id            (root zorunlu)
  data-panel-link   tıklayınca bu panel'e geç
  data-panel-back   geri gitmek istediğin elemente ekle
  data-value        select event'inde gelen değer
  disabled          item'ı devre dışı bırak

EVENTLER:
  'open'    → dropdown açıldı
  'close'   → dropdown kapandı
  'select'  → e.detail: { value, item, panel }

JS API (x-ref ile):
  $refs.myMenu._x_dataStack[0].panelGo('settings')
  $refs.myMenu._x_dataStack[0].close()
*/